import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", account_type: "client" });
  const [busy, setBusy] = useState(false);
  const [brand, setBrand] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get("/public/branding").then((r) => setBrand(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await register(form);
      toast.success("Account created — let's get you set up");
      navigate(u.role === "diy" ? "/diy" : "/portal/onboarding");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAF8] px-6 py-12">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          {brand?.logo_url
            ? <img src={brand.logo_url} alt={brand.company_name} className="h-12 w-12 rounded object-contain" />
            : <ShieldCheck className="h-9 w-9 text-[#0B2545]" />}
          <div className="font-heading font-extrabold text-2xl text-[#0B2545] tracking-tight">{brand?.company_name || "D&Z Management"}</div>
        </div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-1">Start your credit journey</h1>
        <p className="text-sm text-slate-500 mb-6">Create your secure client account. It takes about 2 minutes.</p>
        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <div>
            <Label htmlFor="name">Full legal name</Label>
            <Input id="name" required value={form.name} onChange={set("name")} placeholder="Jordan Smith" data-testid="register-name-input" className="mt-1" />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="register-email-input" className="mt-1" />
          </div>
          <div>
            <Label htmlFor="phone">Phone</Label>
            <Input id="phone" value={form.phone} onChange={set("phone")} placeholder="(555) 555-0100" data-testid="register-phone-input" className="mt-1" />
          </div>
          <div>
            <Label htmlFor="password">Password (min 8 characters)</Label>
            <Input id="password" type="password" required minLength={8} value={form.password} onChange={set("password")} data-testid="register-password-input" className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3" data-testid="account-type-select">
            {[
              ["client", "Full service", "Our team handles your disputes"],
              ["diy", `DIY letters — $${brand?.diy_letter_price ?? 99}/letter`, "Generate & mail yourself · all sales final"],
            ].map(([val, title, sub]) => (
              <button type="button" key={val} onClick={() => setForm({ ...form, account_type: val })}
                data-testid={`account-type-${val}`}
                className={`rounded-lg border-2 p-3 text-left transition-colors duration-150 ${form.account_type === val ? "border-[#0B2545] bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                <div className="text-sm font-semibold text-slate-800">{title}</div>
                <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
              </button>
            ))}
          </div>
          <Button type="submit" disabled={busy} data-testid="register-submit-btn"
            className="w-full bg-[#0B2545] hover:bg-[#134074] text-white font-semibold">
            {busy ? "Creating account…" : "Create account"}
          </Button>
        </form>
        <p className="text-sm text-slate-500 mt-4">
          Already have an account? <Link to="/login" className="text-blue-700 font-medium hover:underline" data-testid="go-login-link">Sign in</Link>
        </p>
        <p className="text-xs text-slate-400 mt-6 leading-relaxed">
          Your information is encrypted in transit and at rest. We never sell your data. You may cancel services within 3 business days under the Credit Repair Organizations Act.
        </p>
      </div>
    </div>
  );
}
